import {
  getAllInvoices,
  getInvoiceByInvoiceID,
  updateInvoice as APIUpdateInvoice,
  createInvoice as APICreateInvoice,
  inheritFromClient as APIIheritFromClient,
  getCollectionInvoices,
  getAllInterestInvoices as APIGetInterestInvoices,
  activateInterestInvoice as APIActivateInterestInvoice,
  deleteInterestInvoice as APIDeleteInterestInvoice,
  getReminders as APIGetReminders,
  activateReminders as APIActivateReminders,
  deleteReminders as APIDeleteReminders,
} from "API/swesum";
import { createContext, useContext, useState } from "react";
import { usePopupNotificationContext } from "./NotificationPopup";

const InvoicesContext = createContext(null);

export const InvoicesContextProvider = ({ children }) => {
  const { openError, openSuccess } = usePopupNotificationContext();
  const [invoices, setInvoices] = useState([]);
  const [interestAccounts, setInterestAccounts] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAllInvoices = async () => {
    setLoading(true);
    const data = await getAllInvoices();
    setInvoices(data);
    setLoading(false);
    return data;
  };

  const getInvoiceById = async (invoiceId) => {
    setLoading(true);
    let invoice = invoices.find(
      (invoice) => Number(invoice.Id) === Number(invoiceId)
    );
    if (!invoice) {
      invoice = await getInvoiceByInvoiceID(invoiceId);
      setInvoices([...invoices, invoice]);
    } else if (!invoice.Lines) {
      invoice = await getInvoiceByInvoiceID(invoiceId);
      const tmpInvoices = invoices.map((i) => {
        if (invoice.Id === i.Id) {
          return invoice;
        }
        return i;
      });
      setInvoices(tmpInvoices);
    }
    setLoading(false);
    return invoice;
  };

  const createInvoice = async (data) => {
    setLoading(true);
    const res = await APICreateInvoice(data);
    if (res.status === 201) {
      setInvoices((prevInvoices) => [...prevInvoices, res.data]);
    }
    setLoading(false);
    return res;
  };

  const updateInvoice = async (invoiceId, body) => {
    const res = await APIUpdateInvoice(invoiceId, body);
    const updatedInvoice = res.data;
    if (res.status === 201) {
      setInvoices((currentInvoices) =>
        currentInvoices.map((invoice) =>
          invoice.Id === updatedInvoice.Id
            ? { ...invoice, ...updatedInvoice }
            : invoice
        )
      );
    }
    return res;
  };

  const inheritFromClient = async (invoiceId) => {
    const res = await APIIheritFromClient(invoiceId);
    const updatedInvoice = res.data.Data;
    if (res.status === 201) {
      setInvoices((currentInvoices) =>
        currentInvoices.map((invoice) =>
          invoice.Id === updatedInvoice.Id
            ? { ...invoice, ...updatedInvoice }
            : invoice
        )
      );
    }
    return res;
  };

  const updateInvoices = (newInvoices) => {
    setInvoices([...invoices, ...newInvoices]);
  };

  const fetchInvoicesForCollection = async () => {
    setLoading(true);
    const res = await getCollectionInvoices();
    setInvoices(res);
    setLoading(false);
  };

  const fetchInvoicesForClient = async (clientId) => {
    setLoading(true);
    setLoading(false);
  };

  const fetchAllInterestInvoices = async () => {
    try {
      setLoading(true);
      const data = await APIGetInterestInvoices();
      setInterestAccounts(data.data);
    } catch (error) {
      openError(`Något gick fel, ${error.message}`, 3000);
    } finally {
      setLoading(false);
    }
  };

  const activateInterestInvoices = async (accounts) => {
    try {
      setLoading(true);
      const { data } = await APIActivateInterestInvoice(accounts);
      return data;
    } catch (error) {
      openError(`Något gick fel, ${error.message}`, 3000);
    } finally {
      setLoading(false);
    }
  };

  const deleteInterestInvoices = async (accounts) => {
    try {
      setLoading(true);
      const { data } = await APIDeleteInterestInvoice(accounts);
      return data;
    } catch (error) {
      openError(`Något gick fel, ${error.message}`, 3000);
    } finally {
      setLoading(false);
    }
  };

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const data = await APIGetReminders();
      setReminders(data.data);
    } catch (error) {
      openError(`Något gick fel, ${error.message}`, 3000);
    } finally {
      setLoading(false);
    }
  };

  const activateReminders = async (accounts) => {
    try {
      setLoading(true);
      const { data } = await APIActivateReminders(accounts);
      return data;
    } catch (error) {
      openError(`Något gick fel, ${error.message}`, 3000);
    } finally {
      setLoading(false);
    }
  };

  const deleteReminders = async (accounts) => {
    try {
      setLoading(true);
      const { data } = await APIDeleteReminders(accounts);
      return data;
    } catch (error) {
      openError(`Något gick fel, ${error.message}`, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <InvoicesContext.Provider
      value={{
        invoices,
        getInvoiceById,
        fetchAllInvoices,
        fetchInvoicesForCollection,
        updateInvoice,
        inheritFromClient,
        createInvoice,
        fetchAllInterestInvoices,
        interestAccounts,
        activateInterestInvoices,
        deleteInterestInvoices,
        fetchReminders,
        reminders,
        activateReminders,
        deleteReminders,
        loading,
      }}
    >
      {children}
    </InvoicesContext.Provider>
  );
};

export const useInvoices = () => useContext(InvoicesContext);
